import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { courseApiSlice } from '../courseApi';
import { ucpPeriods } from '../../../utils/ucpPeriods';
import { setCourse, clearCourse } from '../courseState';
import { settingsApiSlice } from '../../settings/settingsApi';

// Selectors moved from courseSlice
export const selectCourseStatus = (state) => state.course?.status ?? 'idle';
export const selectCourse = (state) => state.course?.data ?? null;
export const selectCourseId = (state) => state.course?.data?.id ?? null;
export const selectCourseToc = (state) => state.course?.data?.toc ?? null;
export const selectCourseTheme = (state) => state.course?.data?.theme ?? null;
export const selectCourseQuiz = (state) => {
  const allQuizes = state.course?.data?.toc?.filter(element => element.type === 'quiz')
  return allQuizes?.reduce((acc, el) => {
    acc = [...acc, el.pages[0]]
    return acc;
  }, [])
}
export const selectCourseQuizSettings = (state) => state.course?.data?.settings?.quiz?.pages ?? null
export const selectUsersAmount = (state) => state.course?.data?.totalUsers ?? 0;

const selectHasUnlimitedDuration = (state) => {
  const selectFetchSettingsResult = settingsApiSlice.endpoints.fetchSettings.select();
  const settingsData = selectFetchSettingsResult(state)?.data;
  return settingsData?.noCourseExpire || false;
};

const selectHideUnlimited = (_, hideUnlimited) => hideUnlimited;

export const selectCourseDurations = createSelector(
  [selectHasUnlimitedDuration, selectHideUnlimited],
  (hasUnlimitedDuration, hideUnlimited) => {
    const allDurations = [...ucpPeriods]
    // Remove unlimited if: user doesn't have noCourseExpire OR we explicitly want to hide it (settings, webshop)
    if (!hasUnlimitedDuration || hideUnlimited) allDurations.shift()
    return allDurations
  }
)

// Mirror Auth's useAuthState pattern with granular, derived selectors

export const useCourseId = () => {
  const course = useSelector(selectCourse);
  return course?.id || null;
};

export const useCourse = () => useSelector(selectCourse);
export const useCourseToc = () => useSelector(selectCourseToc);
export const useCourseDurationsState = (hideUnlimited = false) => useSelector((s) => selectCourseDurations(s, hideUnlimited));
export const useCourseQuizPages = () => useSelector(selectCourseQuizSettings);
export const useCourseTheme = () => useSelector(selectCourseTheme);
export const useCourseStatus = () => useSelector(selectCourseStatus);

// Derived flags
export const useCourseCanEdit = () => !!useSelector(selectCourse)?.canEdit;

// Data hooks using RTKQ with selectFromResult for granularity
export const useCourseById = (courseId) => {
  // Validate courseId to prevent invalid API calls
  const isValidCourseId = courseId &&
    courseId !== 'undefined' &&
    courseId !== 'null' &&
    courseId !== '' &&
    (typeof courseId === 'string' || typeof courseId === 'number');

  // console.log('useCourseById: Hook called', { courseId, isValidCourseId });

  return courseApiSlice.useGetCourseByIdQuery(
    { courseId },
    {
      skip: !isValidCourseId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      selectFromResult: ({ data, isLoading, isFetching, error }) => {
        if (error) {
          console.error('useCourseById: Query error', { courseId, error });
        }
        return {
          course: data || null,
          isLoading,
          isFetching,
          error
        };
      }
    }
  );
};

export const useCourseProgress = (courseId) => {
  return courseApiSlice.useGetCourseProgressQuery({ courseId }, {
    skip: !courseId,
    selectFromResult: ({ data, isLoading, isFetching, error, refetch }) => ({ progress: data || null, isLoading, isFetching, error, refetch })
  });
};

export const useCourseUserCount = (courseId) => {
  return courseApiSlice.useGetCourseUserCountQuery({ courseId }, {
    skip: !courseId,
    selectFromResult: ({ data, isLoading, isFetching, error }) => ({ userCount: data || null, isLoading, isFetching, error })
  });
};

export const useCourseByUserSlug = (username, slugName) => {
  return courseApiSlice.useGetCourseByUserSlugQuery({ username, slugName }, {
    skip: !username || !slugName,
    selectFromResult: ({ data, isLoading, isFetching, error }) => ({ course: data || null, isLoading, isFetching, error })
  });
};

// Export actions for components that need them
export const useSetCourse = () => {
  const dispatch = useDispatch();
  return useCallback((course) => {
    dispatch(setCourse(course));
  }, [dispatch]);
};

export { setCourse, clearCourse };

// Utility hook for clearing course state
export const useClearCourse = () => {
  const dispatch = useDispatch();
  return useCallback(() => {
    console.log('Course: Clearing course state');
    dispatch(clearCourse());
  }, [dispatch]);
};
